import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { getWeeklyStats } from '../../services/api';
import { RefreshCw } from 'lucide-react';

// Brand colors
const BRAND_YELLOW = '#fabc0c';
const BRAND_RED = '#dc2313';
const BRAND_BLACK = '#1b1c1b';

export const RevenueChart = () => {
  const [data, setData] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const weeklyData = await getWeeklyStats();
      
      const chartData = weeklyData.labels.map((label, index) => ({
        name: label,
        revenue: (weeklyData.revenue[index] || 0) / 1000
      }));
      
      setData(chartData);
      setTotalRevenue(weeklyData.revenue.reduce((a, b) => a + b, 0));
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      setData([
        { name: 'Hën', revenue: 144 },
        { name: 'Mar', revenue: 174 },
        { name: 'Mër', revenue: 165 },
        { name: 'Enj', revenue: 187 },
        { name: 'Pre', revenue: 158 },
        { name: 'Sht', revenue: 213 },
        { name: 'Die', revenue: 187 }
      ]);
      setTotalRevenue(1228000);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
            Të Ardhurat Javore
          </h3>
          <p className="text-sm text-gray-500">Në mijëra Lekë</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {(totalRevenue / 1000).toFixed(0)}K
          </p>
          <p className="text-xs text-green-600">Totali i javës</p>
        </div>
      </div>
      {loading ? (
        <div className="h-[250px] flex items-center justify-center">
          <RefreshCw className="w-8 h-8 text-gray-300 animate-spin" />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={BRAND_YELLOW} stopOpacity={0.35}/>
                <stop offset="95%" stopColor={BRAND_YELLOW} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke={BRAND_BLACK} />
            <YAxis tick={{ fontSize: 12 }} stroke={BRAND_BLACK} />
            <Tooltip 
              contentStyle={{ 
                borderRadius: '12px', 
                border: `1px solid ${BRAND_YELLOW}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)' 
              }}
              formatter={(value) => [`${value}K Lekë`, 'Të Ardhura']}
            />
            <Area 
              type="monotone" 
              dataKey="revenue" 
              stroke={BRAND_YELLOW} 
              strokeWidth={2}
              fillOpacity={1} 
              fill="url(#colorRevenue)" 
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export const RidesChart = () => {
  const [data, setData] = useState([]);
  const [totalRides, setTotalRides] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const weeklyData = await getWeeklyStats();
      
      const chartData = weeklyData.labels.map((label, index) => ({
        name: label,
        rides: weeklyData.rides[index] || 0
      }));
      
      setData(chartData);
      setTotalRides(weeklyData.rides.reduce((a, b) => a + b, 0));
    } catch (error) {
      console.error('Error fetching rides data:', error);
      setData([
        { name: 'Hën', rides: 120 },
        { name: 'Mar', rides: 145 },
        { name: 'Mër', rides: 138 },
        { name: 'Enj', rides: 156 },
        { name: 'Pre', rides: 132 },
        { name: 'Sht', rides: 178 },
        { name: 'Die', rides: 156 }
      ]);
      setTotalRides(1025);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
            Udhëtimet Javore
          </h3>
          <p className="text-sm text-gray-500">Numri i udhëtimeve për ditë</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {totalRides}
          </p>
          <p className="text-xs text-green-600">Totali i javës</p>
        </div>
      </div>
      {loading ? (
        <div className="h-[250px] flex items-center justify-center">
          <RefreshCw className="w-8 h-8 text-gray-300 animate-spin" />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke={BRAND_BLACK} />
            <YAxis tick={{ fontSize: 12 }} stroke={BRAND_BLACK} />
            <Tooltip 
              contentStyle={{ 
                borderRadius: '12px', 
                border: `1px solid ${BRAND_RED}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)' 
              }}
              formatter={(value) => [value, 'Udhëtime']}
            />
            <Bar 
              dataKey="rides" 
              fill={BRAND_RED} 
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default { RevenueChart, RidesChart };
